/**
 * TextDrawer.tsx
 * Drawer pour l'ajout et l'édition de texte
 * Extrait de ModernSidebar.tsx
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useAppStore } from '../../../../services/StateService';
import { createDefaultShape } from '../../../../types/ShapeTypes';
import { useAppCoordinatorOptional } from '../../../../hooks/useAppCoordinator';
import { FontSelector } from '../../FontSelector/FontSelector';
import { logger } from '../../../../utils/ProfessionalLogger';

interface TextDrawerProps {
  onClose: () => void;
}

export const TextDrawer: React.FC<TextDrawerProps> = ({ onClose }) => {
  // SUPPRIMÉ: Log dans composant (performance)
  const addShape = useAppStore(state => state.addShape);
  const updateShape = useAppStore(state => state.updateShape);
  // P0.2 FIX: Utiliser selection.primaryId au lieu de canvas.selectedNodeId
  const selectedNodeId = useAppStore(state => state.selection.primaryId);
  const shapes = useAppStore(state => state.shapes);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customText, setCustomText] = useState('Nouveau texte');
  const [customFontSize, setCustomFontSize] = useState(24);
  const [customFontFamily, setCustomFontFamily] = useState('Arial');
  const [customTextColor, setCustomTextColor] = useState('#000000');
  const appCoordinator = useAppCoordinatorOptional();

  // Récupérer le texte sélectionné
  const selectedTextShape = useMemo(() => {
    if (!selectedNodeId) return null;
    const shape = shapes.find(s => s.id === selectedNodeId);
    return shape && shape.type === 'text' ? shape : null;
  }, [selectedNodeId, shapes]);

  // Handler pour mettre à jour le texte sélectionné
  const handleTextUpdate = useCallback(
    (updates: Partial<typeof selectedTextShape>) => {
      if (!selectedTextShape) return;
      // P3 : Partial<ShapeData | null> → Partial<ShapeData> via cast (null exclu par early-return).
      updateShape(selectedTextShape.id, updates as Partial<NonNullable<typeof selectedTextShape>>);
    },
    [selectedTextShape, updateShape]
  );

  const handleAddText = useCallback(
    async (style: 'title' | 'subtitle' | 'body') => {
      logger.info(`[TextDrawer] handleAddText appelé avec style: ${style}`);

      // FIX placement : on positionne TOUJOURS le texte au centre du DOCUMENT
      // (et non du viewport, qui pouvait tomber hors canvas selon le zoom/pan).
      // x/y sont recalculés plus bas une fois la taille du texte connue.
      const shape = createDefaultShape('text', 0, 0);

      if (style === 'title') {
        shape.fontSize = 64;
        shape.fontWeight = 'bold';
        shape.fontFamily = 'Arial';
        shape.text = 'Grand Titre';
        shape.textAlign = 'center';
        shape.fill = '#000000';
        const estimatedWidth = shape.text.length * shape.fontSize * 0.6;
        shape.width = Math.max(estimatedWidth, 200);
        shape.height = shape.fontSize * 1.1;
      } else if (style === 'subtitle') {
        shape.fontSize = 36;
        shape.fontWeight = '600';
        shape.fontFamily = 'Arial';
        shape.text = 'Sous-titre';
        shape.textAlign = 'center';
        shape.fill = '#333333';
        const estimatedWidth = shape.text.length * shape.fontSize * 0.6;
        shape.width = Math.max(estimatedWidth, 150);
        shape.height = shape.fontSize * 1.15;
      } else {
        shape.fontSize = 18;
        shape.fontWeight = '400';
        shape.fontFamily = 'Arial';
        shape.text = 'Votre texte ici';
        shape.textAlign = 'left';
        shape.fill = '#000000';
        const estimatedWidth = shape.text.length * shape.fontSize * 0.6;
        shape.width = Math.max(estimatedWidth, 200);
        shape.height = shape.fontSize * 1.4;
      }

      // Centrer le texte dans le document (toujours visible quel que soit le zoom).
      const { canvas } = useAppStore.getState();
      const docW = canvas.templateWidth || 800;
      const docH = canvas.templateHeight || 600;
      shape.x = Math.max(0, Math.round((docW - (shape.width ?? 0)) / 2));
      shape.y = Math.max(0, Math.round((docH - (shape.height ?? 0)) / 2));
      logger.info(`[TextDrawer] Texte centré dans le document: ${shape.x}, ${shape.y}`);

      try {
        addShape(shape);
        logger.info(`[TextDrawer] Shape ajoutée au store: ${shape.id}`);
        const store = useAppStore.getState();
        logger.info(`[TextDrawer] Nombre de shapes dans le store: ${store.shapes.length}`);
        setTimeout(() => {
          store.setSelectedNode(shape.id);
          logger.info(`[TextDrawer] Shape sélectionnée: ${shape.id}`);
        }, 100);
        onClose();
      } catch (error) {
        logger.error("[TextDrawer] Erreur lors de l'ajout de la shape:", error);
      }
    },
    [addShape, onClose, appCoordinator]
  );

  const handleAddCustomText = useCallback(async () => {
    if (appCoordinator) {
      const fontManager = appCoordinator.getFontManager();
      const systemFonts = [
        'Arial',
        'Helvetica',
        'Times New Roman',
        'Courier New',
        'Georgia',
        'Verdana',
      ];
      if (!systemFonts.includes(customFontFamily) && !fontManager.isFontLoaded(customFontFamily)) {
        await fontManager.loadFontOnDemand(customFontFamily);
      }
    }

    const shape = createDefaultShape('text', 0, 0);
    shape.text = customText || 'Nouveau texte';
    shape.fontSize = customFontSize;
    shape.fontFamily = customFontFamily;
    shape.fill = customTextColor;
    shape.textAlign = 'center';
    const estimatedWidth = (customText || 'Nouveau texte').length * (customFontSize * 0.6);
    shape.width = Math.max(estimatedWidth, 100);
    shape.height = customFontSize * 1.2;

    // Centrer le texte dans le document (toujours visible quel que soit le zoom).
    const { canvas } = useAppStore.getState();
    const docW = canvas.templateWidth || 800;
    const docH = canvas.templateHeight || 600;
    shape.x = Math.max(0, Math.round((docW - (shape.width ?? 0)) / 2));
    shape.y = Math.max(0, Math.round((docH - (shape.height ?? 0)) / 2));

    addShape(shape);
    const store = useAppStore.getState();
    setTimeout(() => {
      store.setSelectedNode(shape.id);
    }, 100);
    onClose();
  }, [
    customText,
    customFontSize,
    customFontFamily,
    customTextColor,
    addShape,
    onClose,
    appCoordinator,
  ]);

  return (
    <>
      <div className="drawer-header">
        <h3 className="drawer-title">Texte</h3>
        <button className="drawer-close-btn" onClick={onClose} aria-label="Fermer">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      <div className="drawer-content">
        {selectedTextShape ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
            <div className="control-group">
              <label>Contenu</label>
              <textarea
                className="input textarea"
                value={selectedTextShape.text || ''}
                onChange={e => handleTextUpdate({ text: e.target.value })}
                rows={3}
              />
            </div>
            <div className="control-group">
              <label>Taille</label>
              <div className="input-with-unit">
                <input
                  type="number"
                  className="input"
                  value={selectedTextShape.fontSize || 24}
                  onChange={e => handleTextUpdate({ fontSize: parseFloat(e.target.value) || 24 })}
                  min={8}
                  max={200}
                />
                <span className="input-unit">px</span>
              </div>
            </div>
            <div className="control-group">
              <label>Police</label>
              <FontSelector
                value={selectedTextShape.fontFamily || 'Arial'}
                onChange={async fontFamily => {
                  if (appCoordinator) {
                    const fontManager = appCoordinator.getFontManager();
                    const systemFonts = [
                      'Arial',
                      'Helvetica',
                      'Times New Roman',
                      'Courier New',
                      'Georgia',
                      'Verdana',
                    ];
                    if (
                      !systemFonts.includes(fontFamily) &&
                      !fontManager.isFontLoaded(fontFamily)
                    ) {
                      await fontManager.loadFontOnDemand(fontFamily);
                    }
                  }
                  handleTextUpdate({ fontFamily });
                }}
              />
            </div>
            <div className="control-group">
              <label>Couleur</label>
              <div className="color-controls">
                <input
                  type="color"
                  className="color-picker"
                  value={selectedTextShape.fill || '#000000'}
                  onChange={e => handleTextUpdate({ fill: e.target.value })}
                />
                <input
                  type="text"
                  className="input"
                  value={(selectedTextShape.fill || '#000000').toUpperCase()}
                  onChange={e => handleTextUpdate({ fill: e.target.value })}
                />
              </div>
            </div>
            <div className="control-group">
              <label>Style</label>
              <div className="button-group">
                <button
                  className={`btn-toggle ${selectedTextShape.fontStyle?.includes('bold') || selectedTextShape.fontWeight === 'bold' ? 'active' : ''}`}
                  onClick={() => {
                    const currentStyle = selectedTextShape.fontStyle || '';
                    const hasBold = currentStyle.includes('bold');
                    const newStyle = hasBold
                      ? currentStyle.replace(/\bbold\b/g, '').trim()
                      : `${currentStyle} bold`.trim();
                    handleTextUpdate({
                      fontStyle: newStyle || 'normal',
                      fontWeight: hasBold ? 'normal' : 'bold',
                    });
                  }}
                  title="Gras"
                >
                  <strong>B</strong>
                </button>
                <button
                  className={`btn-toggle ${selectedTextShape.fontStyle?.includes('italic') ? 'active' : ''}`}
                  onClick={() => {
                    const currentStyle = selectedTextShape.fontStyle || '';
                    const hasItalic = currentStyle.includes('italic');
                    const newStyle = hasItalic
                      ? currentStyle.replace(/\bitalic\b/g, '').trim()
                      : `${currentStyle} italic`.trim();
                    handleTextUpdate({ fontStyle: newStyle || 'normal' });
                  }}
                  title="Italique"
                >
                  <em>I</em>
                </button>
                <button
                  className={`btn-toggle ${selectedTextShape.textDecoration === 'underline' ? 'active' : ''}`}
                  onClick={() => {
                    const newDecoration =
                      selectedTextShape.textDecoration === 'underline' ? 'none' : 'underline';
                    handleTextUpdate({ textDecoration: newDecoration });
                  }}
                  title="Souligné"
                >
                  <u>U</u>
                </button>
              </div>
            </div>
            <div className="control-group">
              <label>Alignement</label>
              <div className="button-group">
                {(['left', 'center', 'right', 'justify'] as const).map(align => (
                  <button
                    key={align}
                    className={`btn-toggle ${selectedTextShape.textAlign === align ? 'active' : ''}`}
                    onClick={() => handleTextUpdate({ textAlign: align })}
                    title={
                      align === 'left'
                        ? 'Gauche'
                        : align === 'center'
                          ? 'Centré'
                          : align === 'right'
                            ? 'Droite'
                            : 'Justifié'
                    }
                  >
                    {align === 'left' && '⬅'}
                    {align === 'center' && '⬌'}
                    {align === 'right' && '➡'}
                    {align === 'justify' && '⬌⬌'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : !showCustomForm ? (
          <>
            <div
              className="text-presets-section"
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}
            >
              <button
                className="drawer-list-item"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  logger.info('[TextDrawer] Bouton "Grand Titre" cliqué');
                  handleAddText('title');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px',
                  background: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  width: '100%',
                  textAlign: 'left',
                  position: 'relative',
                  zIndex: 1,
                  pointerEvents: 'auto',
                }}
              >
                <div
                  className="drawer-list-preview"
                  style={{
                    fontSize: '28px',
                    fontWeight: 'bold',
                    color: 'var(--color-text-primary)',
                    minWidth: '80px',
                    padding: '8px 12px',
                    background: 'var(--color-bg-primary)',
                    borderRadius: '6px',
                    textAlign: 'center',
                    border: '1px solid var(--color-border)',
                  }}
                >
                  Aa
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    Grand Titre
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                    64px · Bold · Centré
                  </span>
                </div>
              </button>

              <button
                className="drawer-list-item"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  logger.info('[TextDrawer] Bouton "Sous-titre" cliqué');
                  handleAddText('subtitle');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px',
                  background: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  width: '100%',
                  textAlign: 'left',
                  position: 'relative',
                  zIndex: 1,
                  pointerEvents: 'auto',
                }}
              >
                <div
                  className="drawer-list-preview"
                  style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: 'var(--color-text-primary)',
                    minWidth: '80px',
                    padding: '8px 12px',
                    background: 'var(--color-bg-primary)',
                    borderRadius: '6px',
                    textAlign: 'center',
                    border: '1px solid var(--color-border)',
                  }}
                >
                  Aa
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    Sous-titre
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                    36px · Semi-Bold · Centré
                  </span>
                </div>
              </button>

              <button
                className="drawer-list-item"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  logger.info('[TextDrawer] Bouton "Corps de texte" cliqué');
                  handleAddText('body');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px',
                  background: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  width: '100%',
                  textAlign: 'left',
                  position: 'relative',
                  zIndex: 1,
                  pointerEvents: 'auto',
                }}
              >
                <div
                  className="drawer-list-preview"
                  style={{
                    fontSize: '14px',
                    fontWeight: '400',
                    color: 'var(--color-text-primary)',
                    minWidth: '80px',
                    padding: '8px 12px',
                    background: 'var(--color-bg-primary)',
                    borderRadius: '6px',
                    textAlign: 'center',
                    border: '1px solid var(--color-border)',
                  }}
                >
                  Aa
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    Corps de texte
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                    18px · Regular · Aligné gauche
                  </span>
                </div>
              </button>
            </div>

            <div
              style={{
                marginTop: 'var(--spacing-lg)',
                paddingTop: 'var(--spacing-lg)',
                borderTop: '1px solid var(--color-border)',
              }}
            >
              <button
                className="drawer-list-item"
                onClick={() => setShowCustomForm(true)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '14px',
                  background: 'var(--color-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  transition: 'all 0.15s ease',
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>Texte personnalisé</span>
              </button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
            <div className="control-group">
              <label>Contenu du texte</label>
              <textarea
                className="input textarea"
                value={customText}
                onChange={e => setCustomText(e.target.value)}
                rows={3}
                placeholder="Tapez votre texte ici..."
                autoFocus
              />
            </div>
            <div className="control-group">
              <label>Taille de police</label>
              <div className="input-with-unit">
                <input
                  type="number"
                  className="input"
                  value={customFontSize}
                  onChange={e =>
                    setCustomFontSize(Math.max(8, Math.min(200, parseInt(e.target.value) || 24)))
                  }
                  min={8}
                  max={200}
                  step={1}
                />
                <span className="input-unit">px</span>
              </div>
            </div>
            <div className="control-group">
              <label>Police</label>
              <FontSelector value={customFontFamily} onChange={setCustomFontFamily} />
            </div>
            <div className="control-group">
              <label>Couleur du texte</label>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
                <input
                  type="color"
                  value={customTextColor}
                  onChange={e => setCustomTextColor(e.target.value)}
                  style={{
                    width: '50px',
                    height: '40px',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                  }}
                />
                <input
                  type="text"
                  className="input"
                  value={customTextColor}
                  onChange={e => setCustomTextColor(e.target.value)}
                  style={{ flex: 1 }}
                  placeholder="#000000"
                />
              </div>
            </div>
            <div
              style={{ display: 'flex', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-md)' }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => setShowCustomForm(false)}
                style={{ flex: 1 }}
              >
                Annuler
              </button>
              <button
                className="btn btn-primary"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  logger.info('[TextDrawer] Bouton "Ajouter" (texte personnalisé) cliqué');
                  handleAddCustomText();
                }}
                style={{
                  flex: 1,
                  position: 'relative',
                  zIndex: 1,
                  pointerEvents: 'auto',
                }}
              >
                Ajouter
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
